import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useSignUp, useUser } from '@clerk/clerk-react';
import { Shield, CheckCircle, Mail, Eye, FileText, Bell, Clock, Star, ArrowRight, ArrowLeft, User, Lock, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Separator } from '../ui/Separator';
import { Badge } from '../ui/Badge';
import { useAnonymousReviewContext } from './AnonymousReviewProvider';
import { AnonymousReviewService } from '../../lib/anonymousReviewService';
import { EmailService } from '../../lib/sendEmailNotification';
import { useAnalytics } from '../../hooks/useAnalytics';

const AnonymousRegister: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded, signUp, setActive } = useSignUp();
  const { data, error, setError } = useAnonymousReviewContext();
  const { user } = useUser();
  const { trackSignUpConversion } = useAnalytics();
  
  const [email, setEmail] = useState(data.creatorEmail || '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [code, setCode] = useState('');

  // If user is already signed in, claim the review and go to final page
  useEffect(() => {
    if (authLoaded && isSignedIn && user) {
      const claimReviewAndNavigate = async () => {
        // Claim the anonymous review if we have one
        if (data.anonymousReviewId) {
          try {
            console.log('User already signed in, claiming anonymous review:', data.anonymousReviewId, 'for user:', user.id);
            await AnonymousReviewService.claimAnonymousReview(data.anonymousReviewId, user.id);
            console.log('Anonymous review claimed successfully');
            
            // Send welcome email to the creator
            try {
              const userName = user.firstName && user.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user.firstName || user.username || 'User';
              const userEmail = user.primaryEmailAddress?.emailAddress || data.creatorEmail;
              
              if (userEmail) {
                await EmailService.sendWelcomeEmail(userEmail, userName);
                console.log('Welcome email sent to creator');
              }
            } catch (emailError) {
              console.error('Error sending welcome email:', emailError);
              // Don't fail if email fails
            }
          } catch (claimError) {
            console.error('Error claiming anonymous review:', claimError);
            // Don't fail the whole flow if claiming fails
          }
        }
        navigate('../final');
      };
      
      claimReviewAndNavigate();
    }
  }, [authLoaded, isSignedIn, user, data.anonymousReviewId, navigate]);

  // Show loading while Clerk is initializing
  if (!authLoaded || !isLoaded) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading registration...</p>
        </div>
      </div>
    );
  }

  const handleGoogleSignUp = async () => {
    if (!signUp) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: window.location.origin + '/anonymous-review/final',
        redirectUrlComplete: window.location.origin + '/anonymous-review/final',
      });
    } catch (err: any) {
      console.error('Error with Google sign up:', err);
      setError('Failed to sign up with Google. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Create the user account
      const result = await signUp.create({
        emailAddress: email,
        password,
      });

      console.log('Sign up created:', result);

      // Send verification email
      await signUp.prepareEmailAddressVerification();
      setPendingVerification(true);
    } catch (err: any) {
      console.error('Error during sign up:', err);
      
      // Handle specific Clerk errors
      if (err.errors && err.errors.length > 0) {
        const errorMessage = err.errors[0].message;
        if (errorMessage.includes('email_address')) {
          setError('This email address is already registered. Please use a different email or try signing in.');
        } else if (errorMessage.includes('password')) {
          setError('Password must be at least 8 characters long and contain a mix of letters and numbers.');
        } else {
          setError(errorMessage);
        }
      } else {
        setError('Failed to create account. Please check your information and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Try to complete the verification
      let completeSignUp;
      try {
        completeSignUp = await signUp.attemptEmailAddressVerification({
          code,
        });
      } catch (verificationError) {
        console.error('Verification attempt failed:', verificationError);
        throw verificationError;
      }

      console.log('Full verification result:', completeSignUp);
      console.log('Verification status:', completeSignUp.status);
      console.log('Verification createdSessionId:', completeSignUp.createdSessionId);
      console.log('Verification createdUserId:', completeSignUp.createdUserId);
      console.log('SignUp status before verification:', signUp.status);
      console.log('SignUp email address:', signUp.emailAddress);
      console.log('SignUp verification strategy:', signUp.verifications.emailAddress.strategy);
      console.log('Verification createdSessionId:', completeSignUp.createdSessionId);
      console.log('Verification createdUserId:', completeSignUp.createdUserId);

      // Check if verification was successful
      if (completeSignUp.status === 'complete' || signUp.status === 'complete' || (completeSignUp.status as any) === 'missing_requirements') {
        // If verification is complete, set the session
        if (completeSignUp.status === 'complete') {
          await setActive({ session: completeSignUp.createdSessionId });
          console.log('Account verified and session created successfully');
          
          // Track sign-up conversion for Google Ads
          trackSignUpConversion();
        } else if (signUp.status === 'complete') {
          console.log('SignUp is complete, proceeding with session creation...');
          // Try to get the session from the signup
          if (signUp.createdSessionId) {
            await setActive({ session: signUp.createdSessionId });
            console.log('Account verified and session created successfully');
            
            // Track sign-up conversion for Google Ads
            trackSignUpConversion();
          } else {
            throw new Error('Could not create session after verification');
          }
        }
        
        // Claim the anonymous review if we have one
        // Get the user ID from either the verification result or the signup
        const userId = completeSignUp.createdUserId || signUp.createdUserId;
        
        if (data.anonymousReviewId && userId) {
          try {
            console.log('Claiming anonymous review:', data.anonymousReviewId, 'for user:', userId);
            await AnonymousReviewService.claimAnonymousReview(data.anonymousReviewId, userId);
            console.log('Anonymous review claimed successfully');
            
            // Send welcome email to the creator
            try {
              const userName = email.split('@')[0] || 'User';
              await EmailService.sendWelcomeEmail(email, userName);
              console.log('Welcome email sent to creator');
            } catch (emailError) {
              console.error('Error sending welcome email:', emailError);
              // Don't fail if email fails
            }
          } catch (claimError) {
            console.error('Error claiming anonymous review:', claimError);
            // Don't fail the whole flow if claiming fails
          }
        }
        
        navigate('../final');
      } else {
        // Handle cases where verification didn't complete successfully
              console.log('Verification status not complete:', completeSignUp.status);
      console.log('Missing requirements detected, checking for additional steps...');
      
      // Check if we need to handle missing requirements
      if (completeSignUp.status === 'missing_requirements' as any) {
        console.log('Missing requirements status detected, attempting to proceed...');
        // Try to proceed anyway since the verification was attempted
        const userId = completeSignUp.createdUserId || signUp.createdUserId;
        
        if (data.anonymousReviewId && userId) {
          try {
            console.log('Claiming anonymous review:', data.anonymousReviewId, 'for user:', userId);
            await AnonymousReviewService.claimAnonymousReview(data.anonymousReviewId, userId);
            console.log('Anonymous review claimed successfully');
            
            // Send welcome email to the creator
            try {
              const userName = email.split('@')[0] || 'User';
              await EmailService.sendWelcomeEmail(email, userName);
              console.log('Welcome email sent to creator');
            } catch (emailError) {
              console.error('Error sending welcome email:', emailError);
              // Don't fail if email fails
            }
          } catch (claimError) {
            console.error('Error claiming anonymous review:', claimError);
            // Don't fail the whole flow if claiming fails
          }
        }
        
        navigate('../final');
        return;
      }
      
      setError('Verification incomplete. Please check the code and try again.');
      }
    } catch (err: any) {
      console.error('Error during verification:', err);
      console.error('Full error object:', JSON.stringify(err, null, 2));
      
      // Handle specific verification errors
      if (err.errors && err.errors.length > 0) {
        const errorMessage = err.errors[0].message;
        console.error('Clerk error message:', errorMessage);
        if (errorMessage.includes('code') || errorMessage.includes('invalid')) {
          setError('Invalid verification code. Please check your email and try again.');
        } else if (errorMessage.includes('already') || errorMessage.includes('verified')) {
          setError('This code has already been used. Please request a new verification code.');
        } else {
          setError(errorMessage);
        }
      } else {
        setError('Verification failed. Please check the code and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!signUp) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      console.log('Resending verification code to:', email);
      console.log('Current signup status:', signUp.status);
      
      await signUp.prepareEmailAddressVerification();
      
      console.log('Verification code resent successfully');
      setError('New verification code sent! Please check your email.');
    } catch (err: any) {
      console.error('Error resending code:', err);
      
      // Handle specific resend errors
      if (err.errors && err.errors.length > 0) {
        const errorMessage = err.errors[0].message;
        setError(`Failed to resend code: ${errorMessage}`);
      } else {
        setError('Failed to resend verification code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipRegistration = () => {
    // For demo purposes, allow skipping registration
    navigate('../final');
  };

  // Email verification screen
  if (pendingVerification) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Mail className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Check Your Email
          </h1>
          <p className="text-muted-foreground">
            We've sent a verification code to <strong>{email}</strong>
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Enter Verification Code</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your email to complete registration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <div>
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                />
              </div>
              
              {/* Error Display for Verification */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={isSubmitting || code.length !== 6}>
                {isSubmitting ? 'Verifying...' : 'Verify Email'}
              </Button>
            </form>
            
            <div className="mt-4 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleResendCode}
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Sending...' : 'Resend Verification Code'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Button variant="outline" onClick={() => setPendingVerification(false)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Registration
          </Button>
        </div>
      </div>
    );
  }



  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <img 
          src="/approvia-logo.png" 
          alt="Approvia" 
          className="h-16 mx-auto mb-4 object-contain"
        />
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Almost There! 🎉
        </h1>
        <p className="text-muted-foreground">
          Create your account to access your review links and manage your reviews
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Benefits Section */}
        <div className="space-y-6">
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <Star className="w-5 h-5" />
                Why Create an Account?
              </CardTitle>
              <CardDescription className="text-blue-700">
                Your review is created! Registration unlocks these benefits:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Access Your Review Links</p>
                  <p className="text-sm text-blue-700">Get the links you need to share with reviewers and manage your review</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Dashboard Access</p>
                  <p className="text-sm text-blue-700">View all your reviews, track progress, and manage settings in one place</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Email Notifications</p>
                  <p className="text-sm text-blue-700">Get notified when reviews are completed or need attention</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Automated Reminders</p>
                  <p className="text-sm text-blue-700">Set up automatic reminder emails to keep your reviews on track and ensure timely feedback</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Increased Daily Limit</p>
                  <p className="text-sm text-blue-700">Create up to 5 reviews per day (instead of 2 for anonymous users)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Review Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Your Review Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Title:</span>
                <span className="font-medium">{data.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reviewer:</span>
                <span className="font-medium">{data.reviewerEmail}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Files:</span>
                <span className="font-medium">
                  {data.files.length > 0 ? `${data.files.length} uploaded` : 'Upload after registration'}
                </span>
              </div>
              {data.deadlineEnabled && data.deadline && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deadline:</span>
                  <span className="font-medium">{new Date(`${data.deadline}T${data.deadlineTime}`).toLocaleDateString()}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Custom Registration Form with Google Login */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Create Your Account
              </CardTitle>
              <CardDescription>
                Quick and secure registration - takes less than 2 minutes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Google Sign Up Button */}
              <Button 
                onClick={handleGoogleSignUp} 
                disabled={isSubmitting}
                variant="outline" 
                className="w-full flex items-center gap-3 py-6 text-base font-medium border-2 hover:bg-gray-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {isSubmitting ? 'Signing up...' : 'Continue with Google'}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with email
                  </span>
                </div>
              </div>

              {/* Email Registration Form */}
              <form onSubmit={handleEmailSignUp} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  {email === data.creatorEmail && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Pre-filled from your review
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a secure password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Must be at least 8 characters long
                  </p>
                </div>

                {/* Error Display for Registration */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    'Creating Account...'
                  ) : (
                    <>
                      Create Account & Get Links
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-xs text-muted-foreground">
                  By creating an account, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Back Button */}
      <div className="flex justify-center mt-8">
        <Button variant="outline" onClick={() => navigate('../step-3')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Preview
        </Button>
      </div>
    </div>
  );
};

export default AnonymousRegister; 